import { Box, Stack, Typography } from '@mui/material';
import { AdmissionAggregateRow, AdmissionArrangeBy, AdmissionMetric } from '@schooly/api';
import { theme } from '@schooly/style';
import type { EChartsOption } from 'echarts';
import { TooltipComponentFormatterCallbackParams } from 'echarts';
import { FC, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useResizeDetector } from 'react-resize-detector';

import SimpleChartBar from '../chartBar/SimpleChartBar';
import { createPlaceholderChartSeries, isSpacerSeries, numberFormatter } from '../utils';
import {
  AdmissionDashboardChartSeriesData,
  dashboardChartBorderRadius,
  dashboardChartConfig,
  transformDashboardRowsToChartSeries,
} from './utils';

type AdmissionDashboardChartsProps = {
  data: AdmissionAggregateRow[];
  arrangeBy: AdmissionArrangeBy;
  selectedMonths: string[];
  currentMetrics: AdmissionMetric[];
  isLoading?: boolean;
};

export const AdmissionDashboardCharts: FC<AdmissionDashboardChartsProps> = ({
  data,
  arrangeBy,
  isLoading,
  selectedMonths,
  currentMetrics,
}) => {
  const { formatMessage } = useIntl();
  const { height: containerHeight, ref: containerRef } = useResizeDetector<HTMLDivElement>();
  const hasNoData = !data.some((row) => row.value) && !isLoading;

  const { chartSeries, categories } = useMemo(() => {
    const isLoadingOrEmpty = isLoading || hasNoData;

    if (isLoadingOrEmpty) {
      const categories =
        arrangeBy === 'month'
          ? selectedMonths
          : currentMetrics.map((metric) => formatMessage({ id: `dashboard-metric-${metric}` }));

      return {
        chartSeries: createPlaceholderChartSeries({
          categories,
          barsCount: arrangeBy === 'metric' ? 2 : 6,
          opacity: !isLoading ? 0 : 1,
          barWidth: dashboardChartConfig.barWidth,
          itemStyle: {
            borderRadius: dashboardChartBorderRadius,
          },
        }),
        categories,
      };
    }

    const { series, categories } = transformDashboardRowsToChartSeries(
      data,
      arrangeBy,
      (id: string) => formatMessage({ id: `dashboard-metric-${id}` }),
      currentMetrics,
    );

    return {
      chartSeries: series,
      categories,
    };
  }, [isLoading, hasNoData, data, arrangeBy, selectedMonths, formatMessage, currentMetrics]);

  const chartTooltip = useCallback((params: TooltipComponentFormatterCallbackParams): string => {
    const seriesName = ('seriesName' in params ? params.seriesName : '') ?? '';
    const dataItem = (
      'data' in params ? params.data : null
    ) as AdmissionDashboardChartSeriesData | null;

    if (Array.isArray(params) || isSpacerSeries(seriesName) || !dataItem) {
      return '';
    }

    const { tooltipData } = dataItem;

    if (!tooltipData) return '';

    const tooltipTextStyle = `font-size: ${theme.typography.body1.fontSize}px; line-height: ${theme.typography.body1.lineHeight}; letter-spacing: ${theme.typography.body1.letterSpacing};`;

    const lines = [];

    for (const item of tooltipData) {
      const formattedValue = typeof item.value === 'number' ? numberFormatter(item.value) : '-';
      lines.push(
        `<div style="${tooltipTextStyle}; color: ${item.color};">${item.label}: ${
          item.value === undefined ? '' : formattedValue
        }</div>`,
      );
    }

    return `<div style="padding: 2px 4px;">${lines.join('')}</div>`;
  }, []);

  const chartOptions = useMemo<EChartsOption>(
    () => ({
      grid: dashboardChartConfig.grid,
      tooltip: {
        trigger: 'item',
        enterable: true,
        showContent: true,
        confine: true,
        borderWidth: 0,
        axisPointer: {
          type: 'shadow',
        },
        formatter: chartTooltip,
      },

      xAxis: [
        {
          type: 'category',
          data: categories,
          axisLine: {
            lineStyle: {
              color: theme.palette.common.light2,
            },
          },
          axisLabel: {
            margin: 20,
            color: theme.palette.common.grey2,
          },
        },
      ],
      yAxis: [
        {
          type: 'value',
          axisLabel: {
            color: theme.palette.common.grey2,
          },
        },
      ],
      legend: {
        data: [],
      },
      series: chartSeries,
      barCategoryGap: dashboardChartConfig.barCategoryGap,
      barGap: dashboardChartConfig.barGap,
    }),
    [categories, chartSeries, chartTooltip],
  );

  const isConversionTab = arrangeBy === 'metric';
  // biome-ignore lint/correctness/useExhaustiveDependencies: Legacy code
  const chartMinHeight = useMemo(() => {
    const minHeight = isConversionTab ? 325 : 400;
    const containerSpacing = isConversionTab ? 144 : 80;
    return containerHeight ? Math.max(minHeight, containerHeight - containerSpacing) : minHeight;
  }, [isConversionTab]);

  return (
    <Stack
      ref={containerRef}
      sx={{
        width: '100%',
        height: '100%',
        minHeight: '400px',
        border: `1px solid ${theme.palette.common.light2}`,
        borderRadius: '8px',
        pt: isConversionTab ? 9 : 5,
        pr: 3,
        pb: isConversionTab ? 9 : 3,
        pl: 1.5,
        position: 'relative',
      }}
    >
      {isConversionTab && (
        <Typography
          variant="h3"
          sx={(theme) => ({
            position: 'absolute',
            top: theme.spacing(2),
            left: theme.spacing(2),
          })}
        >
          {formatMessage({ id: 'dashboard-admissions-pipeline' })}
        </Typography>
      )}
      {hasNoData && <AdmissionEmptyPlaceholder />}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <SimpleChartBar options={chartOptions} minHeight={chartMinHeight} />
      </Box>
    </Stack>
  );
};

const AdmissionEmptyPlaceholder = () => {
  const { formatMessage } = useIntl();
  return (
    <Stack
      sx={{
        zIndex: 1,
        position: 'absolute',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        textAlign: 'center',
        gap: theme.spacing(1),
      }}
    >
      <Typography variant="h1">
        {formatMessage({ id: 'dashboard-admissions-empty-title' })}
      </Typography>
      <Typography variant="h3" color="common.grey2">
        {formatMessage({ id: 'dashboard-admissions-empty-subtitle' })}
      </Typography>
    </Stack>
  );
};
